import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import { useLinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-link-action-mutations";
import { useUnlinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-unlink-action-mutations";
import type { CreateLocalActionRequest } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/hooks/use-action-view";
import type { ActionDTO, Workflow, ActionUpdateBody } from "~/types/api";

export type Props = {
  actions: ActionDTO[];
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
  createActions: (
    workflowId: number,
    actions: CreateLocalActionRequest[]
  ) => Promise<PromiseSettledResult<unknown>[]>;
  isCreating: boolean;
  updateAction: (workflowId: number, actionId: number, body: ActionUpdateBody) => Promise<unknown>;
  isUpdating: boolean;
  deleteAction: (workflowId: number, actionId: number) => Promise<unknown>;
  isDeleting: boolean;
  isTogglingStatuses: boolean;
};

export const actionQueryKey = (workflowId: number) =>
  configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Procedure", {
    params: { path: { workflowId } },
  }).queryKey;

const workflowsQueryKey = configurationApi.queryOptions("get", "/api/Workflow").queryKey;

export const useActionMutations = (workflowIds: number[]): Props => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { linkStatus, isLinking } = useLinkActionStatus();
  const { unLinkStatus, isUnlinking } = useUnlinkActionStatus();

  const { mutateAsync: createAction, isPending: isCreating } = configurationApi.useMutation(
    "post",
    "/api/workflow/{workflowId}/Procedure"
  );

  const { actions, isLoading, error, refetch } = useQueries({
    queries: workflowIds.map((workflowId) => ({
      ...configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Procedure", {
        params: { path: { workflowId } },
      }),
      enabled: workflowId > 0,
      queryKey: actionQueryKey(workflowId),
      meta: { errorTitle: "Failed to load actions" },
    })),
    combine: (results) => {
      const map = new Map<number, ActionDTO>();
      for (const result of results) {
        for (const p of result.data ?? []) {
          if (p && typeof p.id === "number") map.set(p.id, p);
        }
      }
      return {
        actions: Array.from(map.values()),
        isLoading: results.some((q) => q.isLoading || q.isPending),
        error: results.find((q) => q.error)?.error ?? null,
        refetch: async () => {
          await Promise.all(
            workflowIds.map((id) => queryClient.invalidateQueries({ queryKey: actionQueryKey(id) }))
          );
        },
      };
    },
  });

  const createActions = async (
    workflowId: number,
    actions: CreateLocalActionRequest[]
  ): Promise<PromiseSettledResult<unknown>[]> => {
    if (!actions.length) return [];

    await queryClient.cancelQueries({ queryKey: workflowsQueryKey });
    await queryClient.cancelQueries({ queryKey: actionQueryKey(workflowId) });

    const previousWorkflows = queryClient.getQueryData<Workflow[]>(workflowsQueryKey);
    //TODO: Add optimistic update for actions in the workflow. AND change the sortOrder LOGIC!
    try {
      const promises = actions.map(async (action) => {
        const { id: _id, statusCodes, sortOrder, ...body } = action;
        const actionId = await createAction({
          params: { path: { workflowId } },
          body,
        });

        if (statusCodes && statusCodes.length > 0) {
          await Promise.all(
            statusCodes.map((statusCode) =>
              linkStatus(workflowId, actionId, statusCode, sortOrder ?? null)
            )
          );
        }

        return actionId;
      });

      const settled = await Promise.allSettled(promises);

      const failures = settled.filter((r) => r.status === "rejected");
      const fulfilled = settled.filter((r) => r.status === "fulfilled");
      if (failures.length > 0 && fulfilled.length > 0) {
        showToast(
          "warning",
          `${fulfilled.length} action(s) created, ${failures.length} failed.`,
          "Partial success"
        );
        if (previousWorkflows) {
          queryClient.setQueryData(workflowsQueryKey, previousWorkflows);
        }
      } else if (failures.length > 0) {
        const firstReason = (failures[0] as PromiseRejectedResult).reason;
        showToast(
          "error",
          getErrorMessage(firstReason, `${failures.length} action(s) failed to create`),
          "Failed to create actions."
        );
        if (previousWorkflows) {
          queryClient.setQueryData(workflowsQueryKey, previousWorkflows);
        }
      } else if (fulfilled.length > 0) {
        showToast("success", `${fulfilled.length} action(s) created successfully`);
      }

      return settled;
    } catch (err) {
      showToast("error", getErrorMessage(err, "Failed to create actions"), "Error");
      if (previousWorkflows) {
        queryClient.setQueryData(workflowsQueryKey, previousWorkflows);
      }
      return [];
    } finally {
      queryClient.invalidateQueries({ queryKey: workflowsQueryKey });
    }
  };

  const updateActionMutation = configurationApi.useMutation(
    "put",
    "/api/workflow/{workflowId}/Procedure/{procedureId}",
    {
      onSuccess: async (_data, variables) => {
        showToast("success", `Action updated successfully`, `Action ${variables.body.name}`);
        queryClient.invalidateQueries({
          queryKey: actionQueryKey(variables.params.path.workflowId),
        });
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error, "Failed to update action");
        showToast("error", errorMessage, "Failed to update action");
      },
    }
  );

  const deleteActionMutation = configurationApi.useMutation(
    "delete",
    "/api/workflow/{workflowId}/Procedure/{procedureId}",
    {
      onSuccess: async (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: actionQueryKey(variables.params.path.workflowId),
        });
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error, "Failed to delete action");
        showToast("error", errorMessage, "Failed to delete action");
      },
    }
  );

  const updateAction = async (
    workflowId: number,
    actionId: number,
    body: { name: string; configuration: ActionDTO["configuration"] }
  ) => {
    return updateActionMutation.mutateAsync({
      params: { path: { workflowId, procedureId: actionId } },
      body,
    });
  };

  const deleteAction = async (workflowId: number, actionId: number) => {
    const action = actions.find((p) => p.id === actionId);
    const statusesLinked = action?.statuses;
    if (statusesLinked && statusesLinked.length > 0) {
      await Promise.all(
        statusesLinked.map((statusCode) =>
          unLinkStatus(workflowId, actionId, statusCode.statusCode)
        )
      );
    }
    return deleteActionMutation.mutateAsync({
      params: { path: { workflowId, procedureId: actionId } },
    });
  };

  return {
    actions,
    isLoading,
    error,
    refetch,
    createActions,
    isCreating: isCreating,
    updateAction,
    isUpdating: updateActionMutation.isPending,
    deleteAction,
    isDeleting: deleteActionMutation.isPending,
    isTogglingStatuses: isLinking || isUnlinking,
  };
};
